/**
 * v4.4.1 — TourOverlay with intelligent contextual positioning.
 *
 * Improvements over v4.3.1:
 *   * Coach card placement is computed from the spotlight rect — never
 *     covers the target. Priority: right → left → below → above → opposite
 *     corner fallback. On narrow viewports (<720px) the card pins to the
 *     bottom of the screen with horizontal centering so the spotlight area
 *     stays unobstructed.
 *   * Visual emphasis is calmer: 30% dim with a soft contextual gradient,
 *     a 1px inner ring + softer outer glow, and a gentle 3.5s pulse
 *     animation on the ring (linear/stripe/notion register, not gamified).
 *   * Coach card typography breathes more: width 360, line-length ≤ 64ch,
 *     paragraph rhythm consistent across all stops, sticky footer.
 *
 * Operator agency rule (Decision 6 — option b): the dim is *soft*
 * (no pointer-events) so the operator can still click anything underneath.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, X, Play, Pause, Sparkles } from "lucide-react";
import { useDemo } from "./DemoContext";
import { OperationalChip } from "../guidance/OperationalChip";

const SPOTLIGHT_PAD = 14;       // px around the target
const COACH_W = 360;
const COACH_PAD = 24;           // edge padding
const COACH_GAP = 20;           // gap between coach card and spotlight
const COACH_H_ESTIMATE = 360;   // first-paint estimate; remeasured after mount
const MOBILE_W = 720;

const computeRect = (testId) => {
    if (!testId) return null;
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return {
        top: Math.max(0, r.top - SPOTLIGHT_PAD),
        left: Math.max(0, r.left - SPOTLIGHT_PAD),
        width: r.width + SPOTLIGHT_PAD * 2,
        height: r.height + SPOTLIGHT_PAD * 2,
    };
};

/**
 * Compute the optimal coach card placement.
 *
 *  - No spotlight ⇒ centered (welcome stop).
 *  - Mobile      ⇒ pinned bottom, horizontally centered.
 *  - Desktop     ⇒ try right of target → left → below → above → fallback
 *                  to opposite-corner of viewport.
 */
const computePlacement = (spotlight, coachH) => {
    const vw = window.innerWidth || 1280;
    const vh = window.innerHeight || 800;

    // No spotlight (or extremely small) → calm centered card.
    if (!spotlight || (spotlight.width < 80 && spotlight.height < 80)) {
        return {
            position: "fixed",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
        };
    }

    // Narrow viewport: always bottom-centered so the spotlight area stays clear.
    if (vw < MOBILE_W) {
        return {
            position: "fixed",
            bottom: `${COACH_PAD}px`,
            left: "50%",
            transform: "translateX(-50%)",
        };
    }

    const cw = COACH_W;
    const ch = coachH || COACH_H_ESTIMATE;
    const spotRight = spotlight.left + spotlight.width;
    const spotBottom = spotlight.top + spotlight.height;
    const spaceRight = vw - spotRight;
    const spaceLeft = spotlight.left;
    const spaceBelow = vh - spotBottom;
    const spaceAbove = spotlight.top;

    const clampTop = (top) =>
        Math.min(Math.max(top, COACH_PAD), Math.max(COACH_PAD, vh - ch - COACH_PAD));
    const clampLeft = (left) =>
        Math.min(Math.max(left, COACH_PAD), Math.max(COACH_PAD, vw - cw - COACH_PAD));

    // Right of target.
    if (spaceRight >= cw + COACH_GAP + COACH_PAD) {
        return {
            position: "fixed",
            left: `${spotRight + COACH_GAP}px`,
            top: `${clampTop(spotlight.top)}px`,
        };
    }
    // Left of target.
    if (spaceLeft >= cw + COACH_GAP + COACH_PAD) {
        return {
            position: "fixed",
            left: `${spotlight.left - cw - COACH_GAP}px`,
            top: `${clampTop(spotlight.top)}px`,
        };
    }
    // Below the target.
    if (spaceBelow >= ch + COACH_GAP + COACH_PAD) {
        return {
            position: "fixed",
            top: `${spotBottom + COACH_GAP}px`,
            left: `${clampLeft(spotlight.left)}px`,
        };
    }
    // Above the target.
    if (spaceAbove >= ch + COACH_GAP + COACH_PAD) {
        return {
            position: "fixed",
            top: `${spotlight.top - ch - COACH_GAP}px`,
            left: `${clampLeft(spotlight.left)}px`,
        };
    }

    // Final fallback: opposite-corner of where the target's center lies.
    const targetCx = spotlight.left + spotlight.width / 2;
    const targetCy = spotlight.top + spotlight.height / 2;
    const placeOnRight = targetCx < vw / 2;     // target on left → card on right
    const placeOnBottom = targetCy < vh / 2;    // target on top → card on bottom
    return {
        position: "fixed",
        ...(placeOnRight ? { right: `${COACH_PAD}px` } : { left: `${COACH_PAD}px` }),
        ...(placeOnBottom ? { bottom: `${COACH_PAD}px` } : { top: `${COACH_PAD}px` }),
    };
};

export const TourOverlay = () => {
    const { t } = useTranslation();
    const { isActive, persona, step, mode, completed, next, prev, stop, setMode } = useDemo();
    const [spotlight, setSpotlight] = useState(null);
    const [placement, setPlacement] = useState({
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    });
    const coachRef = useRef(null);

    const currentStop = persona?.tour?.[step] || null;

    // Recompute the spotlight rect AND coach placement when:
    //   * the stop changes
    //   * the window resizes/scrolls
    //   * the destination page mounts new content (settle delay)
    useLayoutEffect(() => {
        if (!isActive || !currentStop) {
            setSpotlight(null);
            return;
        }
        let raf, settleTimer, secondPass, scrollTimer;

        const scrollTargetIntoView = () => {
            if (!currentStop.spotlight_testid) return;
            const el = document.querySelector(
                `[data-testid="${currentStop.spotlight_testid}"]`,
            );
            if (!el) return;
            try {
                el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
            } catch (_e) {
                el.scrollIntoView();
            }
        };

        const measure = () => {
            const rect = computeRect(currentStop.spotlight_testid);
            setSpotlight(rect);
            // Use measured coach height when available; otherwise estimate.
            const ch = coachRef.current?.offsetHeight || COACH_H_ESTIMATE;
            setPlacement(computePlacement(rect, ch));
        };

        // Bring the target into view first, then measure once the page
        // has had time to mount + scroll-settle, then a second pass once
        // the coach has been laid out at its real height.
        scrollTimer = setTimeout(scrollTargetIntoView, 80);
        settleTimer = setTimeout(() => {
            measure();
            secondPass = setTimeout(measure, 140);
        }, 500);

        const onResize = () => { raf = requestAnimationFrame(measure); };
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onResize, true);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onResize, true);
            if (raf) cancelAnimationFrame(raf);
            if (scrollTimer) clearTimeout(scrollTimer);
            if (settleTimer) clearTimeout(settleTimer);
            if (secondPass) clearTimeout(secondPass);
        };
    }, [isActive, currentStop]);

    // Keyboard navigation.
    useEffect(() => {
        if (!isActive) return;
        const onKey = (e) => {
            if (e.key === "Escape") stop();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isActive, next, prev, stop]);

    if (!isActive || !persona || !currentStop) return null;
    // v4.8.3 — When the operator has reached the end of the tour we
    // render the calm completion card *in place of* the spotlight +
    // coach overlay. The completion surface lives in its own component
    // (rendered by the OperationalShell) so we simply suppress this
    // overlay's spotlight + coach while completed === true.
    if (completed) return null;

    const coach = currentStop.coach || {};
    const totalSteps = persona.tour.length;

    return (
        <>
            {/* Soft contextual dim — 30% black with subtle radial vignette. */}
            <div
                data-testid="ob-tour-dim"
                className="fixed inset-0 z-40 pointer-events-none transition-opacity duration-300"
                style={{
                    background: spotlight
                        ? `radial-gradient(ellipse at ${spotlight.left + spotlight.width / 2}px ${spotlight.top + spotlight.height / 2}px, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.36) 60%)`
                        : "rgba(0,0,0,0.30)",
                    // Cut the spotlight rect out of the dim entirely.
                    ...(spotlight && {
                        clipPath: `polygon(
                            0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                            ${spotlight.left}px ${spotlight.top}px,
                            ${spotlight.left}px ${spotlight.top + spotlight.height}px,
                            ${spotlight.left + spotlight.width}px ${spotlight.top + spotlight.height}px,
                            ${spotlight.left + spotlight.width}px ${spotlight.top}px,
                            ${spotlight.left}px ${spotlight.top}px
                        )`,
                    }),
                }}
            />

            {/* Spotlight ring — calmer, with gentle pulse. */}
            {spotlight && (
                <div
                    data-testid="ob-tour-spotlight"
                    className="fixed z-40 pointer-events-none rounded-2xl transition-all duration-500 ob-tour-spotlight-pulse"
                    style={{
                        top: spotlight.top, left: spotlight.left,
                        width: spotlight.width, height: spotlight.height,
                    }}
                />
            )}

            {/* Coach card. */}
            <aside
                ref={coachRef}
                role="dialog"
                aria-label={t("ob.tour.coach_aria")}
                data-testid="ob-tour-coach"
                className="z-50 rounded-2xl flex flex-col"
                style={{
                    ...placement,
                    width: COACH_W, maxWidth: "calc(100vw - 32px)",
                    maxHeight: "calc(100vh - 32px)",
                    background: "rgba(13, 18, 28, 0.94)",
                    backdropFilter: "blur(28px) saturate(140%)",
                    WebkitBackdropFilter: "blur(28px) saturate(140%)",
                    border: "1px solid rgba(16,185,129,0.22)",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.02)",
                    color: "var(--ob-text-primary)",
                }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-6 pb-3 flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <Sparkles size={12} strokeWidth={1.75} className="text-emerald-300 flex-shrink-0" />
                        <p
                            className="text-[10px] uppercase tracking-[0.20em] truncate"
                            style={{ color: "#A7F3D0", fontFamily: "Outfit, sans-serif" }}
                            data-testid="ob-tour-eyebrow"
                        >
                            {t("ob.tour.eyebrow", { name: persona.display_name })}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={stop}
                        aria-label={t("ob.tour.exit")}
                        data-testid="ob-tour-exit"
                        className="rounded-full p-1 hover:bg-white/10 transition-colors flex-shrink-0 ml-2"
                        style={{ color: "var(--ob-text-tertiary)" }}
                    >
                        <X size={14} strokeWidth={1.5} />
                    </button>
                </div>

                {/* Scrollable content */}
                <div
                    className="px-6 overflow-y-auto"
                    data-testid="ob-tour-content"
                    style={{ flex: "1 1 auto", minHeight: 0 }}
                >
                    {coach?.attention_level && (
                        <div className="mb-4">
                            <OperationalChip attention={coach.attention_level} testId="ob-tour-attention" />
                        </div>
                    )}

                    {coach?.what && (
                        <p
                            className="mb-3"
                            style={{
                                color: "var(--ob-text-primary)",
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "0.96rem",
                                lineHeight: 1.55,
                                letterSpacing: "0.005em",
                            }}
                            data-testid="ob-tour-what"
                        >
                            {coach.what}
                        </p>
                    )}
                    {coach?.why && (
                        <p
                            className="mb-3"
                            style={{
                                color: "var(--ob-text-secondary)",
                                fontSize: "0.85rem",
                                lineHeight: 1.6,
                            }}
                            data-testid="ob-tour-why"
                        >
                            {coach.why}
                        </p>
                    )}
                    {coach?.what_next && (
                        <p
                            className="mb-3"
                            style={{
                                color: "var(--ob-text-tertiary)",
                                fontSize: "0.78rem",
                                lineHeight: 1.6,
                            }}
                            data-testid="ob-tour-what-next"
                        >
                            {coach.what_next}
                        </p>
                    )}
                    {coach?.safe_to_explore && (
                        <p
                            className="mb-4 italic"
                            style={{
                                color: "#A7F3D0",
                                fontSize: "0.78rem",
                                lineHeight: 1.6,
                            }}
                            data-testid="ob-tour-safe"
                        >
                            {coach.safe_to_explore}
                        </p>
                    )}
                </div>

                {/* Footer (sticky, always visible) */}
                <div
                    className="flex items-center justify-between px-6 pt-4 pb-5 flex-shrink-0"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                >
                    <div className="flex items-center gap-2.5">
                        <button
                            type="button"
                            onClick={prev}
                            disabled={step === 0}
                            aria-label={t("ob.tour.prev")}
                            data-testid="ob-tour-prev"
                            className="rounded-full p-1.5 hover:bg-white/10 disabled:opacity-30 transition-colors"
                            style={{ color: "var(--ob-text-secondary)" }}
                        >
                            <ChevronLeft size={14} strokeWidth={1.75} />
                        </button>
                        <span
                            className="text-xs tabular-nums"
                            style={{ color: "var(--ob-text-tertiary)" }}
                            data-testid="ob-tour-progress"
                        >
                            {t("ob.tour.progress", { current: step + 1, total: totalSteps })}
                        </span>
                        <button
                            type="button"
                            onClick={next}
                            aria-label={t("ob.tour.next")}
                            data-testid="ob-tour-next"
                            className="rounded-full p-1.5 hover:bg-white/10 transition-colors"
                            style={{ color: "var(--ob-text-secondary)" }}
                        >
                            <ChevronRight size={14} strokeWidth={1.75} />
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={() => setMode(mode === "passive" ? "interactive" : "passive")}
                        aria-label={t(mode === "passive" ? "ob.tour.pause" : "ob.tour.play")}
                        data-testid="ob-tour-mode-toggle"
                        data-mode={mode}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 transition-colors"
                        style={{
                            color: mode === "passive" ? "#A7F3D0" : "var(--ob-text-tertiary)",
                            background: mode === "passive" ? "rgba(16,185,129,0.10)" : "transparent",
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        {mode === "passive"
                            ? <><Pause size={10} strokeWidth={1.75} /> {t("ob.tour.auto")}</>
                            : <><Play size={10} strokeWidth={1.75} /> {t("ob.tour.manual")}</>}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default TourOverlay;
